import React from 'react';
import { ClipLoader, BeatLoader, PulseLoader } from 'react-spinners';

/**
 * Skeleton Loader Components
 * Provides various skeleton loading states for better UX
 */

/**
 * SkeletonLoader Component
 * Basic skeleton loader rectangle
 */
export const SkeletonLoader = ({ width = '100%', height = '1rem', className = '' }) => {
  return (
    <div
      className={`animate-pulse bg-gray-200 rounded ${className}`}
      style={{ width, height }}
    />
  );
};

/**
 * SkeletonText Component
 * Multiple lines of skeleton text
 */
export const SkeletonText = ({ lines = 3, className = '' }) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonLoader
          key={index}
          width={index === lines - 1 ? '70%' : '100%'}
          height="0.75rem"
        />
      ))}
    </div>
  );
};

/**
 * SkeletonCard Component
 * Skeleton for card components
 */
export const SkeletonCard = ({ className = '' }) => {
  return (
    <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
      <div className="animate-pulse space-y-4">
        <SkeletonLoader height="1.5rem" width="60%" />
        <SkeletonText lines={2} />
        <div className="flex gap-3 mt-4">
          <SkeletonLoader height="2.5rem" width="5rem" />
          <SkeletonLoader height="2.5rem" width="5rem" />
        </div>
      </div>
    </div>
  );
};

/**
 * SkeletonTable Component
 * Skeleton for table rows
 */
export const SkeletonTable = ({ rows = 5, columns = 4, className = '' }) => {
  return (
    <div className={`bg-white rounded-lg shadow-sm overflow-hidden ${className}`}>
      <div className="animate-pulse">
        {/* Table Header */}
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: columns }).map((_, index) => (
              <SkeletonLoader key={`header-${index}`} height="1rem" />
            ))}
          </div>
        </div>

        {/* Table Rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="border-b border-gray-200 p-4">
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <SkeletonLoader key={`cell-${rowIndex}-${colIndex}`} height="0.875rem" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * SkeletonForm Component
 * Skeleton for form fields
 */
export const SkeletonForm = ({ fields = 4, className = '' }) => {
  return (
    <div className={`space-y-6 ${className}`}>
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="animate-pulse space-y-2">
          <SkeletonLoader height="0.875rem" width="30%" />
          <SkeletonLoader height="2.5rem" width="100%" />
        </div>
      ))}
    </div>
  );
};

/**
 * SkeletonScoreInput Component
 * Skeleton specifically for score input fields
 */
export const SkeletonScoreInput = ({ className = '' }) => {
  return (
    <div className={`animate-pulse space-y-2 ${className}`}>
      <SkeletonLoader height="0.875rem" width="40%" />
      <SkeletonLoader height="2.5rem" width="100%" />
    </div>
  );
};

/**
 * SkeletonScoreGrid Component
 * Skeleton for score input grid
 */
export const SkeletonScoreGrid = ({ columns = 2, rows = 4, className = '' }) => {
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid ${gridClasses[columns]} gap-4 ${className}`}>
      {Array.from({ length: rows }).map((_, index) => (
        <SkeletonScoreInput key={index} />
      ))}
    </div>
  );
};

/**
 * LoadingSpinner Component
 * Centered loading spinner
 */
export const LoadingSpinner = ({
  size = 40,
  color = '#3B82F6',
  text = 'Loading...',
  className = ''
}) => {
  return (
    <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
      <ClipLoader color={color} size={size} />
      {text && <p className="mt-4 text-sm text-gray-600">{text}</p>}
    </div>
  );
};

/**
 * ButtonSpinner Component
 * Small spinner for buttons
 */
export const ButtonSpinner = ({ size = 16, color = '#FFFFFF' }) => {
  return (
    <svg
      className="animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      width={size}
      height={size}
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke={color}
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill={color}
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
};

/**
 * InlineSpinner Component
 * Small inline spinner for text
 */
export const InlineSpinner = ({ size = 16, color = '#3B82F6' }) => {
  return (
    <span className="inline-flex items-center">
      <ClipLoader color={color} size={size} />
    </span>
  );
};

/**
 * LoadingOverlay Component
 * Full-screen or container loading overlay
 */
export const LoadingOverlay = ({
  show,
  text = 'Loading...',
  fullScreen = false,
  className = ''
}) => {
  if (!show) return null;

  const overlayClasses = fullScreen
    ? 'fixed inset-0 z-50'
    : 'absolute inset-0 z-10';

  return (
    <div className={`${overlayClasses} bg-white bg-opacity-90 flex items-center justify-center ${className}`}>
      <div className="text-center">
        <ClipLoader color="#3B82F6" size={50} />
        {text && <p className="mt-4 text-sm font-medium text-gray-700">{text}</p>}
      </div>
    </div>
  );
};

/**
 * LoadingButton Component
 * Button with loading state
 */
export const LoadingButton = ({
  loading,
  disabled,
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  className = '',
  ...props
}) => {
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500',
    success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
    outline: 'border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 focus:ring-primary-500',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        inline-flex items-center justify-center px-4 py-2
        font-semibold rounded-lg
        focus:outline-none focus:ring-2 focus:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors
        ${variantClasses[variant]}
        ${className}
      `}
      {...props}
    >
      {loading && (
        <ButtonSpinner size={16} color={variant === 'outline' ? '#374151' : '#FFFFFF'} />
      )}
      <span className={loading ? 'ml-2' : ''}>{children}</span>
    </button>
  );
};

/**
 * PulseLoader Component
 * Animated pulse loader
 */
export const PulseLoaderComponent = ({
  color = '#3B82F6',
  size = 10,
  className = ''
}) => {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <PulseLoader color={color} size={size} />
    </div>
  );
};

/**
 * BeatLoader Component
 * Animated beat loader
 */
export const BeatLoaderComponent = ({
  color = '#3B82F6',
  size = 10,
  className = ''
}) => {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <BeatLoader color={color} size={size} />
    </div>
  );
};

/**
 * ProgressBar Component
 * Linear progress bar
 */
export const ProgressBar = ({
  progress = 0,
  showLabel = true,
  color = 'bg-primary-600',
  className = ''
}) => {
  const percentage = Math.min(Math.max(progress, 0), 100);

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center justify-between mb-1">
        {showLabel && (
          <span className="text-sm font-medium text-gray-700">{percentage}%</span>
        )}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${color} h-2 rounded-full transition-all duration-300 ease-in-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/**
 * LoadingDots Component
 * Animated loading dots
 */
export const LoadingDots = ({ className = '' }) => {
  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
  );
};

export default {
  SkeletonLoader,
  SkeletonText,
  SkeletonCard,
  SkeletonTable,
  SkeletonForm,
  SkeletonScoreInput,
  SkeletonScoreGrid,
  LoadingSpinner,
  ButtonSpinner,
  InlineSpinner,
  LoadingOverlay,
  LoadingButton,
  PulseLoaderComponent,
  BeatLoaderComponent,
  ProgressBar,
  LoadingDots,
};